<template>
  <div class="mt-3">
    <ValidationObserver ref="formObserver">
      <SubHeaderSection
        title="Información adicional"
        description="Información adicional para el servicio"
        bg-icon="#1d58b7"
        bg-color="#f0f0f0"
      >
        <font-awesome-icon :icon="['far', 'paperclip']" size="lg" fixed-width />
      </SubHeaderSection>

      <div class="sc-sub-body">
        <!-- Fecha de compromiso -->
        <b-row>
          <b-col md="6">
            <ValidationProvider
              ref="provider"
              :rules="{ required: dueDateRequired }"
              name="Fecha de compromiso"
              v-slot="{ errors, valid}"
            >
              <b-form-group>
                <template #label>
                  <span>Fecha de compromiso</span><span v-show="dueDateRequired" class="asterisk">*</span>
                </template>
                <b-input
                  type="date"
                  id="due_on"
                  v-model="form.service.due_on"
                  :min="minDate"
                  :state="(errors[0] || !valid) ? false : true"
                  placeholder="Elegir fecha.."
                  class="form-control white-flatpickr-input w-100"
                />
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col md="6">
            <b-form-group>
              <template #label>
                <span>¿Es un problema recurrente?</span> <span class="asterisk">*</span>
              </template>
              <div class="d-flex">
                <b-button
                  class="mr-2"
                  :variant="form.service.is_recurring == null ? 'outline-danger' : form.service.is_recurring === true ? 'primary' : 'outline-secondary'"
                  size="sm"
                  style="width: 55px;"
                  @click="form.service.is_recurring = true"
                >
                  Sí
                </b-button>
                <b-button
                  :variant="form.service.is_recurring == null ? 'outline-danger' :form.service.is_recurring === false ? 'primary' : 'outline-secondary'"
                  size="sm"
                  style="width: 55px;"
                  @click="form.service.is_recurring = false"
                >
                  No
                </b-button>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Categoría / Unidad -->
        <b-row>
          <b-col md="6">
            <ValidationProvider :rules="{required: true}" name="categoría" v-slot="{ valid, errors }">
              <b-form-group>
                <template #label>
                  <span>Categoría del servicio</span><span class="asterisk">*</span>
                </template>
                <b-select
                  v-model="form.service.sap_series"
                  :options="sapSeriesOptions"
                  :state="(errors[0] || !valid)  ? false : true"
                  @change="onSapSeriesChange"
                >
                  <template v-slot:first>
                    <option :value="null" disabled>-- Seleccionar --</option>
                  </template>
                </b-select>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col md="6">
            <ValidationProvider :rules="{required: true}" name="unidad de negocio" v-slot="{ valid, errors }">
              <b-form-group>
                <template #label>
                  <span>Unidad de negocio</span>
                </template>
                <b-select
                  v-model="form.service.call_type"
                  :options="businessUnitOptions"
                  :state="(errors[0] || !valid)  ? false : true"
                >
                  <template v-slot:first >
                    <option :value="null">-- Seleccionar --</option>
                  </template>
                </b-select>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>

        <!-- serial number -->
         <ValidationProvider :rules="{max: 36}" name="serie" v-slot="{ valid, errors }">
           <b-form-group class="mt-2" label="Número de serie">
             <b-input
               v-model="form.service.item_serial_no"
               type="text"
               id="service-serial_number"
               :state="(errors[0] || !valid)  ? false : true"
               placeholder="Número de serie del equipo"
             />
           </b-form-group>
         </ValidationProvider>

        <b-row v-if="form.client.id">
          <b-col>
            <b-alert variant="info" show class="text-center">
              <font-awesome-icon :icon="['far', 'info-circle']" fixed-width />
              Condición de pago del cliente <b>{{ form.client.payment_terms || 'No definido' }}</b>
            </b-alert>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group>
              <template #label>
                <span>¿Valorar garantía?</span><span class="asterisk">*</span>
              </template>
              <div class="d-flex">
                <b-button
                  class="mr-2"
                  :variant="form.service.warranty_evaluation_required == null ? 'outline-danger' : form.service.warranty_evaluation_required === true ? 'primary' : 'outline-secondary'"
                  size="sm"
                  style="width: 55px;"
                  @click="form.service.warranty_evaluation_required = true"
                >
                  Sí
                </b-button>
                <b-button
                  :variant="form.service.warranty_evaluation_required == null ? 'outline-danger' : form.service.warranty_evaluation_required === false ? 'primary' : 'outline-secondary'"
                  size="sm"
                  style="width: 55px;"
                  @click="form.service.warranty_evaluation_required = false"
                >
                  No
                </b-button>
              </div>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <ValidationProvider :rules="{required: true}" name="forma de pago" v-slot="{ valid, errors }">
              <b-form-group>
                <template #label>
                  <span>Forma de pago</span><span class="asterisk">*</span>
                </template>
                <b-select
                  v-model="form.service.payment_type"
                  :options="paymentTypeAvailableOptions"
                  :state="(errors[0] || !valid)  ? false : true"
                >
                  <template v-slot:first>
                    <option :value="null" disabled>-- Seleccionar --</option>
                  </template>
                </b-select>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>

        <b-alert :show="form.client.id && form.service.warranty_evaluation_required === true" variant="info" class="mt-2 text-center fs-15">
          <router-link :to="{ path: `/app/prod/clientes/${form.client.id}` }" custom v-slot="{ href, navigate }">
            <a :href="href" target="_blank" rel="noopener noreferrer" @click="navigate">
              <span class="pl-1">Ver la información de las últimas órdenes del cliente</span>
              <font-awesome-icon :icon="['far', 'arrow-right']" fixed-width />
            </a>
          </router-link>
        </b-alert>

        <ValidationProvider :rules="{required: true, min: 5, max: 199}" name="comentarios" v-slot="{ valid, errors }">
          <b-form-group class="mt-2">
            <template #label>
              <span>Comentarios internos</span>
              <span class="asterisk">*</span>
            </template>
            <b-textarea
              placeholder="Comentarios internos de la solicitud de servicio"
              v-model="form.service.comments"
              rows="4"
              :state="(errors[0] || !valid)  ? false : true"
            />
          </b-form-group>
        </ValidationProvider>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import SubHeaderSection from '../SubHeaderSection'
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ClientDashboards from '../../clients/ClientDashboards.vue';
import {
  sapSeriesOptions,
  paymentTypeOptions,
  businessUnits,
  setDefaultBusinessUnit
} from '../../../../sharedOptions/serviceCallModule'

export default {
  name: 'ThirdStep',
  components: { SubHeaderSection, ValidationObserver, ValidationProvider, ClientDashboards},
  props: {
    form: { type: Object, required: true },
  },
  data () {
    return {
      sapSeriesOptions: sapSeriesOptions,
      paymentTypeOptions: paymentTypeOptions,
      businessUnits : businessUnits
    }
  },
  computed: {
    businessUnitOptions () {
      return this.businessUnits.sort((a, b) => a.text.localeCompare(b.text, 'es'))
    },
    minDate () {
      return new Date()?.toISOString()?.split('T')[0]
    },
    dueDateRequired () {
      return this.form.service.sap_series == 61
    },
    paymentTypeAvailableOptions () {
      if (this.form.client?.payment_terms === 'Contado') {
        return [
          { value: 'Sinpe',  text: 'Sinpe' },
          { value: 'Tarjeta',  text: 'Tarjeta' },
          { value: 'Efectivo',  text: 'Efectivo' },
          { value: 'Transferencia',  text: 'Transferencia' }
        ]
      }

      return this.paymentTypeOptions
    }
  },
  methods: {
    // forms
    async checkFormValidity () {
      return await this.$refs.formObserver.validate()
    },
    onSapSeriesChange (val) {
      this.form.service.call_type = setDefaultBusinessUnit(val)
    }
  }
}
</script>

<style scoped>
  .sc-sub-body { padding: 6px 24px 24px; }

  .red-input-border { border: 1px solid #dc2626; }
  .green-input-border { border: 1px solid #29A745; }

  @media (max-width: 640px) {
    .sc-sub-body { padding-left: 16px; padding-right: 16px; }
  }
</style>
